import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Form, Alert, Spinner, Table, Modal } from 'react-bootstrap';
import axios from 'axios';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CATEGORIES_ID_NAME_MAP, BASE_URL } from '../assets/constant';

// Canvas Signature Pad Component
const SignaturePad = ({ onSave, initialImage }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  useEffect(() => {
    if (initialImage) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasSignature(true);
      };
      img.src = initialImage;
    }
  }, [initialImage]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSignature) {
      const canvas = canvasRef.current;
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureData(null);
  };

  const saveSignature = () => {
    if (!signatureData) return;
    fetch(signatureData)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], `signature-${Date.now()}.png`, {
          type: 'image/png'
        });
        onSave(file);
      });
  };

  return (
    <div className="signature-pad-wrapper">
      <div className="signature-pad-container">
        <canvas
          ref={canvasRef}
          width={500}
          height={200}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          style={{
            border: '2px solid #ced4da',
            borderRadius: '8px',
            cursor: 'crosshair',
            backgroundColor: '#fff',
            width: '100%',
            height: 'auto',
            touchAction: 'none'
          }}
        />
        <canvas
          style={{ display: 'none' }}
          onTouchStart={(e) => {
            e.preventDefault();
            const touch = e.touches[0];
            const canvas = canvasRef.current;
            const mouseEvent = new MouseEvent('mousedown', {
              clientX: touch.clientX,
              clientY: touch.clientY
            });
            canvas.dispatchEvent(mouseEvent);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            const touch = e.touches[0];
            const canvas = canvasRef.current;
            const mouseEvent = new MouseEvent('mousemove', {
              clientX: touch.clientX,
              clientY: touch.clientY
            });
            canvas.dispatchEvent(mouseEvent);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            const canvas = canvasRef.current;
            const mouseEvent = new MouseEvent('mouseup', {});
            canvas.dispatchEvent(mouseEvent);
          }}
        />
      </div>
      <div className="signature-actions mt-2">
        <Button
          type="button"
          onClick={clearCanvas}
          className="signature-action-btn secondary"
        >
          <i className="fas fa-undo me-2"></i>
          Clear
        </Button>
        <Button
          type="button"
          onClick={saveSignature}
          disabled={!hasSignature}
          className="signature-action-btn primary ms-2"
        >
          <i className="fas fa-check me-2"></i>
          Save Signature
        </Button>
      </div>
      <small className="text-muted mt-1">
        <i className="fas fa-info-circle me-1"></i>
        Draw your signature using mouse or touch
      </small>
    </div>
  );
};

SignaturePad.propTypes = {
  onSave: PropTypes.func.isRequired,
  initialImage: PropTypes.string
};

// Main SignatureManager Component
export const SignatureManager = ({
  userId,
  recallId,
  recallType: propRecallType,
  onSignatureUpdate
}) => {
  // State
  const [signatures, setSignatures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingSignature, setEditingSignature] = useState(null);
  const [formData, setFormData] = useState({
    userId: userId || '',
    recallId: recallId || '',
    recallType: propRecallType || 1,
    submitedBy: '',
    docImage: null
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [signatureToDelete, setSignatureToDelete] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [signatureMethod, setSignatureMethod] = useState('draw');
  const fileInputRef = useRef(null);
  const token = useSelector((state) => state.auth?.token);

  // Update formData when props change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      userId: userId || prev.userId,
      recallId: recallId || prev.recallId,
      recallType: propRecallType || prev.recallType
    }));
  }, [userId, recallId, propRecallType]);

  // Fetch signatures with filters
  const fetchSignatures = async () => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build URL with filters
      let url = `${BASE_URL}/signature?userId=${userId}`;

      // Add recallId filter if provided
      if (recallId) {
        url += `&recallId=${recallId}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success) {
        setSignatures(response.data.data || []);
        setImageErrors({});
      }
    } catch (error) {
      console.error('Error fetching signatures:', error);
      setError(error.response?.data?.message || 'Failed to load signatures');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmitSignature = async (e) => {
    e.preventDefault();

    if (!formData.docImage && !editingSignature) {
      setError('Please provide a signature');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const formDataToSend = new FormData();
    formDataToSend.append('userId', formData.userId);
    formDataToSend.append('recallId', formData.recallId);
    formDataToSend.append('recallType', formData.recallType);
    if (formData.submitedBy) {
      formDataToSend.append('submitedBy', formData.submitedBy);
    }
    if (formData.docImage) {
      formDataToSend.append('docImage', formData.docImage);
    }

    try {
      let url, method;
      if (editingSignature) {
        url = `${BASE_URL}/signature/${editingSignature.id}`;
        method = "put";
      } else {
        url = `${BASE_URL}/signature`;
        method = "post";
      }

      const response = await axios({
        method: method,
        url: url,
        data: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data?.success) {
        setSuccess(editingSignature ? 'Signature updated successfully!' : 'Signature created successfully!');
        await fetchSignatures();
        if (onSignatureUpdate) onSignatureUpdate();
        resetForm();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      setError(error.response?.data?.message || 'Failed to save signature');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete handlers
  const showDeleteConfirmation = (signature) => {
    setSignatureToDelete(signature);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!signatureToDelete) return;

    setIsSubmitting(true);
    setError(null);
    setShowDeleteModal(false);

    try {
      const url = `${BASE_URL}/signature/${signatureToDelete.id}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Signature deleted successfully!');
      await fetchSignatures();
      if (onSignatureUpdate) onSignatureUpdate();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error deleting signature:', error);
      setError('Failed to delete signature');
    } finally {
      setIsSubmitting(false);
      setSignatureToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSignatureToDelete(null);
  };

  // Edit handler
  const handleEditSignature = (signature) => {
    setEditingSignature(signature);
    setFormData({
      userId: signature.user_id || userId,
      recallId: signature.recall_id || recallId,
      recallType: parseInt(signature.recall_type) || propRecallType || 1,
      submitedBy: signature.submited_by || '',
      docImage: null
    });
    setPreviewUrl(signature.doc_image_url || null);
    setShowForm(true);
    setSignatureMethod('draw');
  };

  // Reset form
  const resetForm = () => {
    setEditingSignature(null);
    setFormData({
      userId: userId || '',
      recallId: recallId || '',
      recallType: propRecallType || 1,
      submitedBy: '',
      docImage: null
    });
    setPreviewUrl(null);
    setShowForm(false);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // File change handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('File size must be less than 2MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      setFormData({ ...formData, docImage: file });
      setPreviewUrl(URL.createObjectURL(file));
      setError(null);
    }
  };

  // Signature pad handler
  const handleSignatureFromPad = (file) => {
    setFormData({ ...formData, docImage: file });
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  // Image error handler
  const handleImageError = (signatureId) => {
    setImageErrors(prev => ({
      ...prev,
      [signatureId]: true
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get recall type name helper
  const getRecallTypeName = (id) => {
    const numericId = typeof id === 'string' ? parseInt(id) : id;
    const found = CATEGORIES_ID_NAME_MAP.find(item => item.id === numericId);
    return found ? found.name : id;
  };

  // Fetch signatures when userId or recallId changes
  useEffect(() => {
    if (userId) {
      fetchSignatures();
    }
  }, [userId, recallId]);

  return (
    <div className="signature-manager">
      {/* Header */}
      <div className="signature-header">
        <div className="header-left">
          <i className="fas fa-file-signature header-icon"></i>
          <div>
            <h5 className="mb-0">Manage Signatures</h5>
            <small className="text-muted">
              {/* {signatures.length} saved {recallId ? `for recall #${recallId}` : ''} */}
            </small>
          </div>
        </div>
        {!showForm && (
          <Button
            className="add-signature-btn"
            onClick={() => setShowForm(true)}
          >
            <i className="fas fa-plus me-2"></i> Add Signature
          </Button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mt-3">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mt-3">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {/* Signature Form */}
      {showForm && (
        <div className="signature-form-container">
          <div className="form-header">
            <h6 className="mb-0">
              {editingSignature ? 'Edit Signature' : 'Add New Signature'}
            </h6>
            <button className="form-close-btn" onClick={resetForm}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <Form onSubmit={handleSubmitSignature}>
            <Form.Group className="form-group">
              <Form.Label>Submitted By</Form.Label>
              <Form.Control
                type="text"
                value={formData.submitedBy}
                onChange={(e) => setFormData({ ...formData, submitedBy: e.target.value })}
                placeholder="Enter name of submitter"
                className="form-control-custom"
              />
            </Form.Group>

            <input type="hidden" name="recallType" value={formData.recallType} />

            {/* Signature Method Selection */}
            <Form.Group className="form-group">
              <Form.Label>Signature Method</Form.Label>
              <div className="signature-method-selector">
                <Button
                  type="button"
                  onClick={() => setSignatureMethod("draw")}
                  className={`signature-method-btn ${signatureMethod === "draw" ? "active" : ""}`}
                >
                  <i className="fas fa-pencil-alt me-2"></i>
                  Draw
                </Button>
                <Button
                  type="button"
                  onClick={() => setSignatureMethod("upload")}
                  className={`signature-method-btn ${signatureMethod === "upload" ? "active" : ""}`}
                >
                  <i className="fas fa-upload me-2"></i>
                  Upload
                </Button>
              </div>
            </Form.Group>

            {/* Draw Signature */}
            {signatureMethod === 'draw' && (
              <Form.Group className="form-group">
                <Form.Label>Draw Your Signature</Form.Label>
                <SignaturePad
                  onSave={handleSignatureFromPad}
                  initialImage={previewUrl}
                />
                {formData.docImage && (
                  <div className="mt-2 text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    Signature captured successfully!
                  </div>
                )}
              </Form.Group>
            )}

            {/* Upload Signature */}
            {signatureMethod === 'upload' && (
              <Form.Group className="form-group">
                <Form.Label>Upload Signature Image</Form.Label>
                <div className="file-upload-wrapper">
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-upload-info">
                    <small className="text-muted">Max file size: 2MB (PNG, JPG, JPEG)</small>
                  </div>
                </div>

                {previewUrl && (
                  <div className="preview-container mt-2">
                    <img
                      src={previewUrl}
                      alt="Signature preview"
                      className="preview-image"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '100px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                )}
              </Form.Group>
            )}

            <div className="form-actions mt-3">
              <Button
                className="submit-btn"
                type="submit"
                disabled={isSubmitting || !formData.docImage}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingSignature ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {editingSignature ? 'Update Signature' : 'Create Signature'}
                  </>
                )}
              </Button>
              <Button variant="outline-secondary signature-cancel-btn" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}

      {/* Signatures Table */}
      {isLoading ? (
        <div className="loading-container">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading signatures...</p>
        </div>
      ) : signatures.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-file-signature empty-icon"></i>
          <h6>No Signatures Found</h6>
          <p className="text-muted">
            {recallId
              ? `No signatures found for recall #${recallId}`
              : 'Click "Add Signature" to upload your first signature'}
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table className="signature-table">
            <thead>
              <tr>
                {/* <th>Sl. No.</th> */}
                <th>Signature</th>
                <th>Submitted By</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((sig, index) => {
                const hasError = imageErrors[sig.id];

                return (
                  <tr key={sig.id} className="signature-row">
                    {/* <td className="row-number">{index + 1}</td> */}
                    <td>
                      {sig.doc_image && !hasError ? (
                        <img
                          src={sig.doc_image_url}
                          alt="Signature"
                          className="signature-thumbnail"
                          onClick={() => window.open(sig.doc_image_url, "_blank")}
                          onError={() => handleImageError(sig.id)}
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          style={{
                            width: '80px',
                            height: '40px',
                            objectFit: 'contain',
                            cursor: 'pointer',
                            border: '1px solid #eee',
                            borderRadius: '4px',
                            padding: '4px'
                          }}
                        />
                      ) : (
                        <div className="signature-placeholder d-flex align-items-center gap-2">
                          <i className="fas fa-file-image text-muted"></i>
                          <span className="text-muted small">
                            {hasError ? 'Failed to load' : 'No image'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="submitter-info">
                        <i className="fas fa-user-circle text-muted me-2"></i>
                        {sig.submited_by || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="date-info">
                        <i className="far fa-calendar-alt me-1"></i>
                        {formatDate(sig.created_at)}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons d-flex justify-content-center gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="action-btn edit-btn"
                          onClick={() => handleEditSignature(sig)}
                          title="Edit signature"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="action-btn delete-btn"
                          onClick={() => showDeleteConfirmation(sig)}
                          title="Delete signature"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={cancelDelete}
        centered
        className="delete-confirmation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-danger me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Are you sure you want to delete this signature?
          </p>
          {signatureToDelete && (
            <div className="delete-details">
              <div className="delete-item">
                <strong>Submitted By:</strong> {signatureToDelete.submited_by || 'N/A'}
              </div>
              <div className="delete-item">
                <strong>Created:</strong> {formatDate(signatureToDelete.created_at)}
              </div>
              {signatureToDelete.doc_image && (
                <div className="delete-image-preview mt-2">
                  <img
                    src={signatureToDelete.doc_image_url}
                    alt="Signature to delete"
                    style={{
                      maxWidth: '100px',
                      maxHeight: '100px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <p className="text-muted small mt-2">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-2"></i>
                Yes, Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

SignatureManager.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  recallId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  recallType: PropTypes.number,
  onSignatureUpdate: PropTypes.func
};

SignatureManager.defaultProps = {
  userId: '',
  recallId: '',
  recallType: 1,
  onSignatureUpdate: () => { }
};

export default SignatureManager;